/**
 * Stripe via the official loader (Stripe.js is fetched from js.stripe.com, never bundled).
 * Card data lives only inside Stripe's iframe element. The SetupIntent client secret comes
 * from Automaid's `POST /setup/billing/setup_intent`; we confirm it here and hand the
 * `seti_…` id to the controller.
 */
import { loadStripe, type Stripe, type StripeCardElement, type StripeCardElementChangeEvent } from '@stripe/stripe-js';

export interface BillingDetails {
  name: string;
  email: string;
  phone?: string;
  address?: { line1?: string; city?: string; state?: string; postal_code?: string; country?: string };
}

export type StripeStatus = 'idle' | 'loading' | 'ready' | 'unavailable';

export interface StripeBridge {
  status(): StripeStatus;
  subscribe(listener: () => void): () => void;
  /** Loads Stripe.js for the tenant's publishable key (idempotent). */
  load(publishableKey: string | null | undefined): Promise<void>;
  /** Mounts a Card Element into `container`; returns an unmount function. */
  mountCard(container: HTMLElement, onChange: (event: { complete: boolean; error: string | null }) => void): () => void;
  /** Confirms the SetupIntent with the mounted card; resolves with the SetupIntent id. */
  confirmCardSetup(clientSecret: string, billing: BillingDetails): Promise<string>;
  /** Creates a PaymentMethod from the mounted card (gift cards); resolves with the `pm_…` id. */
  createPaymentMethod(billing: Pick<BillingDetails, 'name' | 'email'>): Promise<string>;
  /** Runs the SCA challenge for a PaymentIntent (gift cards); resolves with the `pi_…` id. */
  handleCardAction(clientSecret: string): Promise<string>;
}

export interface StripeBridgeOptions {
  /** injectable for tests */
  loader?: (key: string) => Promise<Stripe | null>;
  appearance?: { fontFamily?: string; color?: string; placeholderColor?: string };
}

export function createStripeBridge(options: StripeBridgeOptions = {}): StripeBridge {
  const loader = options.loader ?? ((key: string) => loadStripe(key));
  let status: StripeStatus = 'idle';
  let stripe: Stripe | null = null;
  let card: StripeCardElement | null = null;
  let loading: Promise<void> | null = null;
  const listeners = new Set<() => void>();
  const setStatus = (s: StripeStatus) => {
    status = s;
    for (const l of listeners) l();
  };

  return {
    status: () => status,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    load(publishableKey) {
      if (loading) return loading;
      if (!publishableKey) {
        setStatus('unavailable');
        return Promise.resolve();
      }
      setStatus('loading');
      loading = loader(publishableKey)
        .then((instance) => {
          stripe = instance;
          setStatus(instance ? 'ready' : 'unavailable');
        })
        .catch(() => setStatus('unavailable'));
      return loading;
    },
    mountCard(container, onChange) {
      if (!stripe) return () => {};
      const elements = stripe.elements();
      const element = elements.create('card', {
        hidePostalCode: true,
        style: {
          base: {
            fontFamily: options.appearance?.fontFamily ?? "'museo-sans-rounded', 'Nunito', system-ui, sans-serif",
            fontSize: '16px',
            color: options.appearance?.color ?? '#272f3a',
            '::placeholder': { color: options.appearance?.placeholderColor ?? '#8a96a3' },
          },
          invalid: { color: '#db323b' },
        },
      });
      element.on('change', (event: StripeCardElementChangeEvent) => onChange({ complete: event.complete, error: event.error?.message ?? null }));
      element.mount(container);
      card = element;
      return () => {
        element.unmount();
        element.destroy();
        if (card === element) card = null;
      };
    },
    async confirmCardSetup(clientSecret, billing) {
      if (!stripe || !card) throw new Error('Card payments are not available right now.');
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card, billing_details: { name: billing.name, email: billing.email, phone: billing.phone, address: billing.address } },
      });
      if (result.error) throw new Error(result.error.message ?? 'Your card could not be verified.');
      if (!result.setupIntent?.id) throw new Error('Card setup did not complete.');
      return result.setupIntent.id;
    },
    async createPaymentMethod(billing) {
      if (!stripe || !card) throw new Error('Card payments are not available right now.');
      const result = await stripe.createPaymentMethod({
        type: 'card',
        card,
        billing_details: { name: billing.name, email: billing.email },
      });
      if (result.error) throw new Error(result.error.message ?? 'Your card could not be verified.');
      if (!result.paymentMethod?.id) throw new Error('Your card could not be verified.');
      return result.paymentMethod.id;
    },
    async handleCardAction(clientSecret) {
      if (!stripe) throw new Error('Card payments are not available right now.');
      const result = await stripe.handleCardAction(clientSecret);
      if (result.error) throw new Error(result.error.message ?? 'Your card could not be verified.');
      if (!result.paymentIntent?.id) throw new Error('Your card could not be verified.');
      return result.paymentIntent.id;
    },
  };
}
